import type { AnySelfOwnedWidgetDefinition, LegacyWidgetDefinition } from '../WidgetDefinition';
import { accountsWidgetDefinition } from './accounts';
import { platformsWidgetDefinition, symbolsWidgetDefinition, tradersWidgetDefinition } from './aggregated-risk';
import { alertsWidgetDefinition } from './alerts';
import { alphaEyeWidgetDefinition } from './alpha-eye';
import { backtestWidgetDefinition } from './backtest';
import { basketTradingWidgetDefinition } from './basket-trading';
import { eventsWidgetDefinition } from './events';
import { executionsWidgetDefinition } from './executions';
import { filingsWidgetDefinition } from './filings';
import { locatesV2WidgetDefinition } from './locates-v2';
import { manualReviewWidgetDefinition } from './manual-review';
import { messagesWidgetDefinition } from './messages';
import { newsWidgetDefinition } from './news';
import { notepadWidgetDefinition } from './notepad';
import { orderBlotterWidgetDefinition } from './order-blotter';
import { ordersWidgetDefinition } from './orders';
import { positionsWidgetDefinition } from './positions';
import { resultsWidgetDefinition } from './results';
import { scannerWidgetDefinition } from './scanner';
import { screenerWidgetDefinition } from './screener';
import { searchAlphaWidgetDefinition } from './searchalpha';
import { tableWidgetDefinition } from './table';
import { tradeProWidgetDefinition } from './trade-pro';
import { tradeProMultiWidgetDefinition } from './trade-pro-multi';
import { tradingViewChartWidgetDefinition } from './tradingview-chart';
import { watchlistWidgetDefinition } from './watchlist';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { i18n } from 'i18next';
import { useTranslation } from 'react-i18next';

export type WidgetDefinitionEntry = LegacyWidgetDefinition | AnySelfOwnedWidgetDefinition;
export type RegisteredWidgetType = Exclude<WidgetType, 'alpha-lens'>;

export const allWidgets: Record<RegisteredWidgetType, WidgetDefinitionEntry> = {
    accounts: accountsWidgetDefinition,
    alerts: alertsWidgetDefinition,
    backtest: backtestWidgetDefinition,
    'alpha-eye': alphaEyeWidgetDefinition,
    'basket-trading': basketTradingWidgetDefinition,
    events: eventsWidgetDefinition,
    executions: executionsWidgetDefinition,
    filings: filingsWidgetDefinition,
    lab: locatesV2WidgetDefinition,
    'manual-review': manualReviewWidgetDefinition,
    messages: messagesWidgetDefinition,
    news: newsWidgetDefinition,
    notepad: notepadWidgetDefinition,
    'order-blotter': orderBlotterWidgetDefinition,
    orders: ordersWidgetDefinition,
    platforms: platformsWidgetDefinition,
    positions: positionsWidgetDefinition,
    results: resultsWidgetDefinition,
    scanner: scannerWidgetDefinition,
    screener: screenerWidgetDefinition,
    searchalpha: searchAlphaWidgetDefinition,
    symbols: symbolsWidgetDefinition,
    table: tableWidgetDefinition,
    'trade-pro': tradeProWidgetDefinition,
    'trade-pro-multi': tradeProMultiWidgetDefinition,
    'tradingview-chart': tradingViewChartWidgetDefinition,
    traders: tradersWidgetDefinition,
    watchlist: watchlistWidgetDefinition,
};

export function getWidgetDefinition(type: WidgetType): WidgetDefinitionEntry {
    if (type === 'alpha-lens') return alphaEyeWidgetDefinition;
    return allWidgets[type];
}

export type WidgetNameKey = WidgetDefinitionEntry['nameKey'];
export type WidgetCategoryKey = WidgetDefinitionEntry['category'];

export const WIDGET_CATEGORY_ORDER: readonly WidgetCategoryKey[] = [
    'widgets::categories::research',
    'widgets::categories::trading',
    'widgets::categories::risk',
    'widgets::categories::misc',
];

interface WidgetNameDerivable {
    type: WidgetType;
}

// TODO: Relocate these functions to a more appropriate location
export function useWidgetDisplayName({ type }: WidgetNameDerivable): string {
    return useTranslation().t(getWidgetDefinition(type).nameKey);
}

// TODO: Relocate these functions to a more appropriate location
export function getWidgetDisplayName(t: i18n['t'], { type }: WidgetNameDerivable): string {
    return t(getWidgetDefinition(type).nameKey);
}
